import { type EditorView } from '@progress/kendo-editor-common'
import { Dialog } from '@progress/kendo-react-dialogs'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  DocumentImageInfiniteScrollList,
  type DocumentImageInfiniteScrollListProps,
} from '~/components/features/DocumentImageInfiniteScrollList'
import { useLogEvent } from '~/hooks/useLogEvent'
import { compile as rootCompile } from '~/routes/paths'
import { exists } from '~/utils'

export type InsertDocumentImageDialogProps = {
  onClose: () => void
  view: EditorView
}

export const InsertDocumentImageDialog = ({
  onClose,
  view,
}: InsertDocumentImageDialogProps) => {
  const { t } = useTranslation()
  const logEvent = useLogEvent()

  const handleCardClick = React.useCallback<
    EH<DocumentImageInfiniteScrollListProps, 'onCardClick'>
  >(
    ({ comment, id, imageURL, restoreKey, title }) => {
      const {
        state: {
          schema,
          schema: {
            marks: { link: linkMarkType },
            nodes: {
              hard_break: hardBreakNodeType,
              image: imageNodeType,
              paragraph: paragraphNodeType,
            },
          },
          tr,
        },
      } = view

      if (exists(comment) && 0 < comment.length) {
        // Preserve textarea line breaks as hard breaks within ONE paragraph,
        // mirroring the generated path (useGenerateDocuments renders the comment
        // as a single <p> with \n → <br />). schema.text('') throws, so empty
        // lines contribute a hard break only.
        const commentContent: ReturnType<typeof schema.text>[] = []
        comment.split('\n').forEach((line, index) => {
          if (0 < index) {
            commentContent.push(hardBreakNodeType.create())
          }
          if (0 < line.length) {
            commentContent.push(schema.text(line))
          }
        })
        const commentParagraphNode = paragraphNodeType.createAndFill(
          {},
          commentContent,
        )
        if (exists(commentParagraphNode)) {
          tr.replaceSelectionWith(commentParagraphNode)
        }
      }

      const imageNode = imageNodeType.createAndFill({
        alt: id,
        src: imageURL,
        title,
      })
      if (exists(imageNode)) {
        // image is an inline node; wrap it in its own paragraph so it always
        // starts on a new line, regardless of the comment's length/line count.
        const imageParagraphNode = paragraphNodeType.createAndFill(
          {},
          imageNode,
        )
        if (exists(imageParagraphNode)) {
          tr.replaceSelectionWith(imageParagraphNode)
        }
      }

      const sharedUrl = `${window.location.origin}${rootCompile('restore', {
        key: restoreKey,
      })}`
      const linkMark = linkMarkType.create({ href: sharedUrl })
      const textNode = schema.text(title, [linkMark])
      const paragraphNode = paragraphNodeType.createAndFill({}, textNode)
      if (exists(paragraphNode)) {
        tr.replaceSelectionWith(paragraphNode)
      }

      view.dispatch(tr)

      onClose()

      logEvent('Insert saved table and graph')
    },
    [logEvent, onClose, view],
  )

  return (
    <Dialog
      appendTo={document.body}
      closeIcon={true}
      title={t('insert_saved_table_graph')}
      onClose={onClose}
    >
      <DocumentImageInfiniteScrollList onCardClick={handleCardClick} />
    </Dialog>
  )
}
